import { Container } from '@src/elements/Grid';
import { TextCardBlockFragment } from '@src/graphql/gql-types';
import styles from './TextCardBlock.module.scss';
import React from 'react';
import Heading from '@src/elements/Heading';
import RichTextRender from '../RichTextRender';
import { ContentfulPageTypes } from '@src/types/contentful';
import { ContentBlocksData } from '@src/types/contentBlocks';
import { RICHTEXT_RENDER_MODE } from '@src/types/richText';
import LinkWrapper from '../LinkWrapper';
import { getCTA } from '@src/utils/url';
import {
  COLOR_OUTERSPACE,
  ContentBlockBackgroundColors
} from '@src/types/colors';
import { ButtonLinkStandard } from '../ButtonLink';
import BoxIcon from '@src/elements/BoxIcon';
import classNames from 'classnames';
import { getColorClasses } from '@src/utils/colorClasses';

type TextCardBlock = TextCardBlockFragment & {
  pageType: ContentfulPageTypes;
  sharedBuildData?: ContentBlocksData;
  backgroundColor?: ContentBlockBackgroundColors;
};

const TextCardBlock: React.FC<TextCardBlock> = ({
  tagline,
  heading,
  bodyContent,
  primaryCta,
  textCardItemsCollection,
  pageType,
  sharedBuildData,
  backgroundColor
}) => {
  const replacementDictionary = sharedBuildData?.replacementDictionary;

  return (
    <div
      className={classNames(
        styles.textCardBlock,
        backgroundColor ? getColorClasses(backgroundColor, styles) : ''
      )}
    >
      <Container containerWidth="fluid">
        <p className={styles.tagline}>{tagline}</p>

        <Heading priority={2} fontStyle="h1">
          {heading}
        </Heading>

        {bodyContent && (
          <div className={styles.richTextWrapper}>
            <RichTextRender
              bodyContent={bodyContent}
              pageType={pageType}
              renderMode={RICHTEXT_RENDER_MODE.NO_BLOCK_EMBEDS}
              replacementDictionary={replacementDictionary}
            />
          </div>
        )}

        {primaryCta && (
          <ButtonLinkStandard
            {...getCTA(primaryCta)}
            color={COLOR_OUTERSPACE}
            size="md"
            className={styles.primaryCta}
          >
            {primaryCta.label}
            {/* TODO: move to ButtonStandard "icon" prop once that's switched to BoxIcon */}
            <BoxIcon name="right-arrow-alt" size="sm" />
          </ButtonLinkStandard>
        )}

        <div className={styles.textCardItemsWrapper}>
          {textCardItemsCollection?.items.map((item) => (
            <div className={styles.textCardItem} key={item?.sys.id}>
              {item?.primaryCta ? (
                <LinkWrapper
                  {...getCTA(item?.primaryCta)}
                  className={styles.textCardItemLinkWrapper}
                >
                  <div>
                    <Heading priority={4} fontStyle="h2">
                      {item?.heading}
                    </Heading>
                    <p className={styles.textCardItemDescription}>
                      {item?.description}
                    </p>
                  </div>

                  <p className={styles.textCardItemLink}>
                    {item?.primaryCta.label}
                    <BoxIcon name="right-arrow-alt" size="sm" />
                  </p>
                </LinkWrapper>
              ) : (
                <div className={styles.textCardItemLinkWrapperNonLink}>
                  <Heading priority={4} fontStyle="h2">
                    {item?.heading}
                  </Heading>
                  <p className={styles.textCardItemDescription}>
                    {item?.description}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </Container>
    </div>
  );
};

export default TextCardBlock;
